'use client'

import { createContext, useContext, ReactNode, useEffect, useState } from 'react'
import { useAuth as useAuthHook } from '@/hooks/use-auth'
import { usePathname } from 'next/navigation'
import type { AuthState, LoginCredentials } from '@/types/auth'
import GoogleOneTap from './GoogleOneTap'

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>
  logout: () => Promise<boolean>
  refreshToken: () => Promise<boolean>
  sendGoogleSession: (user: any) => Promise<boolean>
  executeRecaptcha: (action?: string) => Promise<string | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuthHook()
  const pathname = usePathname()
  const [shouldShowOneTap, setShouldShowOneTap] = useState(false)

  // Determinar si debemos mostrar Google One Tap
  useEffect(() => {
    // No mostrar si está cargando o si ya está autenticado
    if (auth.loading || auth.isAuthenticated) {
      setShouldShowOneTap(false)
      return
    }

    // No mostrar en páginas específicas donde no debería aparecer
    const excludedPaths = [
      '/signup',
      '/forgot-password',
      '/reset-password',
      // [athlete-register] El deportista llega aquí desde el email a fijar su contraseña; un One Tap
      // encima del formulario le ofrecería entrar con Google a una cuenta que no es la suya.
      '/create-password',
      '/verify-email'
    ]

    const shouldExclude = excludedPaths.some(path =>
      pathname.includes(path)
    )

    // Mostrar One Tap solo si:
    // - No está autenticado
    // - No está cargando
    // - No estamos en páginas excluidas
    setShouldShowOneTap(!shouldExclude)
  }, [auth.loading, auth.isAuthenticated, pathname])

  // Extraer el idioma del pathname
  const getLanguageFromPathname = () => {
    const segments = pathname.split('/')
    return segments[1] || 'es' // Por defecto español
  }

  return (
    <AuthContext.Provider value={auth}>
      {children}

      {/* Google One Tap Sign-In Global */}
      {shouldShowOneTap && (
        <GoogleOneTap
          clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}
          lang={getLanguageFromPathname()}
          autoPrompt={true}
          onSuccess={() => {
            // console.log('Google One Tap global: Login exitoso')
          }}
          onError={(error) => {
            console.error('Google One Tap global error:', error)
          }}
        />
      )}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 